import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useParams } from "react-router-dom";
import { api } from "../../api";
import usePageTitle from "../../hooks/usePageTitle";
import AppShell from "../AppShell";
import ImageWithFallback from "../ImageWithFallback";
import { buildItemUrl } from "../../utils/itemUrl";
import { formatPrice, getPriceDollars } from "../../utils/price";
import { PriceVisibilityBoundary, usePriceVisibility } from "../../contexts/PriceVisibilityContext";
import { useAdaptiveHeroGradient } from "../../utils/adaptiveHeroGradient";
import {
  buildSportsSetImageCandidates,
  getSportsSetConfig,
  normalizeSportKey,
} from "../../utils/sportsSets";

const panelSx = {
  p: { xs: 2, md: 2.5 },
  borderRadius: 2.5,
  border: "1px solid",
  borderColor: "rgba(12, 35, 64, 0.08)",
  backgroundColor: "#ffffff",
  boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
};

const cardSx = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  p: 1.4,
  borderRadius: 2.25,
  border: "1px solid",
  borderColor: "rgba(12, 35, 64, 0.08)",
  backgroundColor: "#fbfcff",
  textDecoration: "none",
  color: "inherit",
  transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease, background-color 160ms ease",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 16px 30px rgba(15, 23, 42, 0.12)",
    borderColor: "rgba(32, 110, 92, 0.20)",
    backgroundColor: "#ffffff",
  },
};

function PlaceholderImage({ label = "No Image" }) {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "text.secondary",
        fontWeight: 700,
        letterSpacing: 0.3,
      }}
    >
      {label}
    </Box>
  );
}

function SportsSetItemCard({ item, setEntry, sport }) {
  const setImages = buildSportsSetImageCandidates(setEntry, sport);
  const primaryImage = item?.image_url || item?.ref_image_url || setImages[0] || "";
  const useImage = Boolean(getSportsSetConfig(sport).fallbackImages.length || setEntry?.imageFile || item?.image_url || item?.ref_image_url);

  return (
    <Box component={RouterLink} to={buildItemUrl(item)} sx={cardSx}>
      <Box
        sx={{
          height: 210,
          borderRadius: 1.75,
          overflow: "hidden",
          backgroundColor: "#eef3f8",
          mb: 1.35,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {useImage && primaryImage ? (
          <ImageWithFallback
            src={primaryImage}
            fallbackSrcs={setImages}
            alt={item.name || `${setEntry?.set_name || "Sports set"} card`}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <PlaceholderImage label={getSportsSetConfig(sport).shortTitle} />
        )}
      </Box>

      <Stack spacing={0.55} sx={{ flexGrow: 1 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 800,
            color: "#10233f",
            lineHeight: 1.3,
            minHeight: 48,
            wordBreak: "break-word",
          }}
        >
          {item?.name || `Item #${item?.item_id}`}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
          {item?.set_name || setEntry?.set_name}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {item?.number ? `No. ${item.number}` : "Number unavailable"}
        </Typography>
        <PriceVisibilityBoundary><Typography variant="body1" sx={{ color: "primary.main", fontWeight: 800, pt: 0.35 }}>
          {formatPrice(item) || "TBD"}
        </Typography></PriceVisibilityBoundary>
      </Stack>
    </Box>
  );
}

export default function SportsSetTemplatePage({ sport: sportProp, setSlug: setSlugProp }) {
  const { showPrices: pricePreferenceEnabled, pricesLoaded } = usePriceVisibility();
  const showPrices = pricesLoaded && pricePreferenceEnabled;
  const routeParams = useParams();
  const sport = normalizeSportKey(sportProp || routeParams.sport || "");
  const config = getSportsSetConfig(sport);
  const setSlug = String(setSlugProp || routeParams.setSlug || "").trim().toLowerCase();

  const initialSetEntry = useMemo(() => ({
    slug: setSlug,
    set_name: "Sports Set",
    sport: config.key,
    item_count: 0,
    priced_count: 0,
  }), [config.key, setSlug]);

  const [loadedSetEntry, setLoadedSetEntry] = useState(null);
  const setEntry = loadedSetEntry || initialSetEntry;
  const safeSetName = setEntry?.set_name || config.title;
  const imageCandidates = useMemo(() => buildSportsSetImageCandidates(setEntry, sport), [setEntry, sport]);
  const heroVisual = useAdaptiveHeroGradient(imageCandidates, config.heroGradient, { maxSize: 200 });
  const useHeroImage = Boolean(config.fallbackImages.length || setEntry?.imageFile);

  usePageTitle(safeSetName);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortBy, setSortBy] = useState(() => showPrices ? "price_desc" : "item_id_desc");
  useEffect(() => { if (!showPrices && sortBy.startsWith("price_")) setSortBy("item_id_desc"); }, [showPrices, sortBy]);

  useEffect(() => {
    let mounted = true;

    const loadSet = async () => {
      if (!sport || !setSlug) {
        if (mounted) {
          setLoadedSetEntry(null);
          setItems([]);
          setError("");
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        setError("");
        const { data } = await api.get(`/api/sports/${sport}/sets/${setSlug}/items`, {
          params: {
            sort: sortBy,
          },
        });
        if (!mounted) return;
        const nextSet = data?.set || data?.item || null;
        const nextItems = Array.isArray(data?.items) ? data.items : [];
        setLoadedSetEntry(
          nextSet
          || (nextItems.length
            ? {
                slug: setSlug,
                sport: config.key,
                set_name: nextItems[0]?.set_name || config.title,
                item_count: nextItems.length,
                priced_count: nextItems.filter((item) => getPriceDollars(item) != null).length,
              }
            : initialSetEntry)
        );
        setItems(nextItems);
      } catch (err) {
        if (!mounted) return;
        if (err?.response?.status === 404) {
          setLoadedSetEntry(initialSetEntry);
          setItems([]);
          setError(`No backend route is returning cards for ${config.title} yet.`);
          return;
        }
        setLoadedSetEntry(initialSetEntry);
        setItems([]);
        setError(err?.response?.data?.error || err?.response?.data?.message || err?.message || `Failed to load ${config.title}.`);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadSet();
    return () => {
      mounted = false;
    };
  }, [config.key, config.title, initialSetEntry, setSlug, sortBy, sport]);

  const pricedCount = useMemo(
    () => items.filter((item) => getPriceDollars(item) != null).length,
    [items]
  );

  const highestPrice = useMemo(() => {
    const values = items.map((item) => getPriceDollars(item)).filter((value) => value != null);
    if (!values.length) return "";
    return formatPrice({ price: Math.max(...values).toFixed(2) });
  }, [items]);

  if (!sport || !setSlug) {
    return (
      <AppShell>
        <Box sx={{ px: { xs: 0, md: 1 }, py: { xs: 1, md: 1.5 } }}>
          <Paper elevation={0} sx={panelSx}>
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              This sports set page is not configured yet.
            </Alert>
          </Paper>
        </Box>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <Box sx={{ px: { xs: 0, md: 1 }, py: { xs: 1, md: 1.5 } }}>
        <Stack spacing={{ xs: 2.5, md: 3 }}>
          <Paper
            elevation={0}
            sx={{
              ...panelSx,
              p: { xs: 2.5, md: 3.25 },
              background: heroVisual.background,
              borderColor: "rgba(255,255,255,0.10)",
              position: "relative",
              overflow: "hidden",
              transition: "background 360ms ease",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(rgba(0,0,0,0.20), rgba(0,0,0,0.20)), radial-gradient(circle at top right, rgba(255,255,255,0.16), transparent 30%), radial-gradient(circle at bottom left, rgba(255,255,255,0.10), transparent 26%)",
                pointerEvents: "none",
              }}
            />
            <Grid container spacing={3} sx={{ position: "relative", zIndex: 1 }} alignItems="center">
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    height: { xs: 220, md: 300 },
                    borderRadius: 2.25,
                    overflow: "hidden",
                    border: "1px solid rgba(255,255,255,0.14)",
                    backgroundColor: "rgba(255,255,255,0.10)",
                    boxShadow: `0 0 44px ${heroVisual.glowColor}`,
                    transition: "box-shadow 360ms ease",
                  }}
                >
                  {useHeroImage ? (
                    <ImageWithFallback
                      src={imageCandidates[0]}
                      fallbackSrcs={imageCandidates.slice(1)}
                      alt={safeSetName}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                  ) : (
                    <PlaceholderImage label={config.shortTitle} />
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography
                  variant="overline"
                  sx={{
                    display: "block",
                    color: "rgba(255,245,228,0.76)",
                    fontWeight: 800,
                    letterSpacing: 1.15,
                    mb: 1,
                  }}
                >
                  Sports Set Browse
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    color: "#ffffff",
                    fontWeight: 900,
                    lineHeight: 1.08,
                    mb: 1.1,
                    maxWidth: 760,
                    fontSize: { xs: "2rem", md: "2.7rem" },
                  }}
                >
                  {safeSetName}
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255,244,226,0.86)",
                    maxWidth: 800,
                    lineHeight: 1.65,
                    fontSize: { xs: "0.98rem", md: "1.02rem" },
                    mb: 1.75,
                  }}
                >
                  Browse {safeSetName} with set details, loaded cards, and pricing in one clean collector view.
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip label={config.shortTitle} sx={{ fontWeight: 700, bgcolor: config.chipBg, color: "#fff" }} />
                  <Chip label={`${setEntry.item_count || items.length || 0} cards`} sx={{ fontWeight: 700, bgcolor: config.chipBg, color: "#fff" }} />
                  {showPrices && setEntry.priced_count ? <Chip label={`${setEntry.priced_count} priced`} sx={{ fontWeight: 700, bgcolor: config.chipBg, color: "#fff" }} /> : null}
                  {showPrices && pricedCount ? <Chip label={`${pricedCount} with prices`} sx={{ fontWeight: 700, bgcolor: config.chipBg, color: "#fff" }} /> : null}
                  {showPrices && highestPrice ? <Chip label={`Top price ${highestPrice}`} sx={{ fontWeight: 700, bgcolor: config.chipBg, color: "#fff" }} /> : null}
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {loading ? (
            <Paper elevation={0} sx={{ ...panelSx, py: { xs: 5, md: 7 } }}>
              <Stack spacing={1.5} alignItems="center" justifyContent="center">
                <CircularProgress size={34} />
                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                  Loading {safeSetName} cards...
                </Typography>
              </Stack>
            </Paper>
          ) : error ? (
            <Paper elevation={0} sx={panelSx}>
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            </Paper>
          ) : (
            <Paper elevation={0} sx={panelSx}>
              <Stack spacing={1.1} sx={{ mb: 2.25 }}>
                <Typography variant="h5" sx={{ fontWeight: 900, color: config.accent }}>
                  {safeSetName} Cards
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 860, lineHeight: 1.65 }}>
                  Browse every {safeSetName} card currently loaded, with direct links to each item page.
                </Typography>
              </Stack>
              <Box
                sx={{
                  display: "flex",
                  alignItems: { xs: "stretch", sm: "center" },
                  justifyContent: "space-between",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1.5,
                  mb: 2.25,
                }}
              >
                <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 700 }}>
                  {items.length} {items.length === 1 ? "Card" : "Cards"}
                </Typography>
                <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 220 } }}>
                  <InputLabel id={`${setSlug || "sports-set"}-sort-label`}>Sort Items</InputLabel>
                  <Select
                    labelId={`${setSlug || "sports-set"}-sort-label`}
                    value={sortBy}
                    label="Sort Items"
                    onChange={(event) => setSortBy(event.target.value)}
                  >
                    {showPrices && <MenuItem value="price_desc">Price High to Low</MenuItem>}
                    {showPrices && <MenuItem value="price_asc">Price Low to High</MenuItem>}
                    <MenuItem value="name_asc">Name A-Z</MenuItem>
                    <MenuItem value="name_desc">Name Z-A</MenuItem>
                    <MenuItem value="newest">Newest</MenuItem>
                    <MenuItem value="oldest">Oldest</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {items.length === 0 ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    borderColor: "rgba(12, 35, 64, 0.08)",
                    backgroundColor: "#f9fbfd",
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#10233f", mb: 0.5 }}>
                    No {safeSetName} cards are available yet
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    No inventory has been returned for this set yet.
                  </Typography>
                </Paper>
              ) : (
                <Grid container spacing={{ xs: 1.5, md: 2 }}>
                  {items.map((entry) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={entry.item_id || `${entry.name}-${entry.number}`}>
                      <SportsSetItemCard item={entry} setEntry={setEntry} sport={sport} />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Paper>
          )}
        </Stack>
      </Box>
    </AppShell>
  );
}
